import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { API } from '@/lib/api';
import { BRAND } from '@/config/brand';
import { SERVICES, PORTFOLIO_ITEMS, TESTIMONIALS, AWARDS, STATS, IMAGES } from '@/data/content';

const DEFAULT_SETTINGS = {
  heroImage: IMAGES.hero,
  founderImage: IMAGES.founder,
  tagline: BRAND.tagline,
  phone: BRAND.phone,
  phoneDisplay: BRAND.phoneDisplay,
  whatsapp: BRAND.whatsapp,
  email: BRAND.email,
  instagram: BRAND.instagram,
  youtube: BRAND.youtube,
  city: BRAND.city,
  stats: STATS,
};

const DEFAULT_CATEGORIES = ['Weddings', 'Birthdays', 'Baby Showers', 'Cradle Ceremonies', 'Devotional', 'Corporate', 'Community', 'School Events'].map((c) => ({
  label: c,
  slug: c.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
}));

const DEFAULT_CALCULATOR = { price_2d: 550, price_3d: 1100, min_2d: 8, min_3d: 16, tiers: [] };

const FALLBACK = { settings: DEFAULT_SETTINGS, services: SERVICES, portfolio: PORTFOLIO_ITEMS, testimonials: TESTIMONIALS, awards: AWARDS, categories: DEFAULT_CATEGORIES, packages: [], packageCategories: [], calculator: DEFAULT_CALCULATOR };

const Ctx = createContext(null);

export const ContentProvider = ({ children }) => {
  // Start empty: never show bundled placeholder content once real content exists.
  const [content, setContent] = useState({ settings: DEFAULT_SETTINGS, services: [], portfolio: [], testimonials: [], awards: [], categories: [], packages: [], calculator: DEFAULT_CALCULATOR });
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`${API}/content`);
      const data = await res.json();
      setContent({
        settings: { ...DEFAULT_SETTINGS, ...(data.settings || {}) },
        services: data.services || [],
        portfolio: data.portfolio || [],
        testimonials: data.testimonials || [],
        awards: data.awards || [],
        categories: data.categories?.length ? data.categories : DEFAULT_CATEGORIES,
        packages: data.packages || [],
        packageCategories: data.package_categories || [],
        calculator: { ...DEFAULT_CALCULATOR, ...(data.calculator || {}) },
      });
    } catch (e) {
      // Only on network failure do we fall back to bundled defaults.
      setContent(FALLBACK);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const s = content.settings;
  const brand = {
    ...BRAND,
    tagline: s.tagline || BRAND.tagline,
    phone: s.phone || BRAND.phone,
    phoneDisplay: s.phoneDisplay || BRAND.phoneDisplay,
    whatsapp: s.whatsapp || BRAND.whatsapp,
    email: s.email || BRAND.email,
    instagram: s.instagram || BRAND.instagram,
    youtube: s.youtube || BRAND.youtube,
    city: s.city || BRAND.city,
  };

  return <Ctx.Provider value={{ ...content, brand, loaded, refresh }}>{children}</Ctx.Provider>;
};

export const useContent = () => useContext(Ctx);
